import join from 'lodash/join'
import { useCallback, useContext, useEffect, useState } from 'react'

import { useAmplitude } from '@Analytics/hook'
import { wishListStateManager } from '@Domains/WishList/StateManager'
import { useProductAnalyticsLazyQuery } from '@Generated/graphql'
import { generateEventID } from '@Globals/Google/DataLayer'
import { gtagEvent } from '@Globals/Google/GTag'
import { MetadataContext } from '@Globals/Metadata'

export function useWishList(key: string, sourceID: string = 'inherit') {
  const [exists, setExists] = useState<boolean>(false)
  const ampli = useAmplitude()
  const { store } = useContext(MetadataContext)
  const [fetchAnalytics] = useProductAnalyticsLazyQuery()

  useEffect(() => {
    if (!key) {
      return () => {}
    }

    return wishListStateManager.subscribeExists(key, result => setExists(result))
  }, [key])

  const toggle = useCallback(() => {
    const result = wishListStateManager.toggle(key)

    fetchAnalytics({
      variables: {
        id: key,
      },
    }).then(({ data }) => {
      if (!data?.product) {
        return
      }

      const { product } = data

      const categories: string[] = []

      const categoryMain = product.primaryCategories?.[0]?.name || ''
      if (categoryMain) {
        categories.push(categoryMain)
      }

      const categorySub = product.primaryCategories?.[1]?.name || ''
      if (categorySub) {
        categories.push(categorySub)
      }

      const analytics = {
        brand: product?.brand?.title || '',
        categories: join(categories, '/'),
        category_ids: `-${join(product?.categoryIDs, '-')}-`,
        category_main: categoryMain,
        category_sub: categorySub,
        has_video: !!(product as any)?.videoAsset?.url,
        id: product?.id || '',
        is_new: (product as any)?.masterVariant?.isNew?.value === true,
        is_on_sale: (product?.masterVariant?.price?.discount?.value || 0) > 0,
        personalisation_type: '',
        quantity: 1,
        source_id: sourceID,
      }

      if (result) {
        gtagEvent('add_to_wishlist', {
          event_id: generateEventID(),
          currency: store.config?.currencyCode,
          items: [
            {
              item_id: product.id,
              item_brand: product?.brand?.title || '',
              item_category: product.primaryCategories?.[0]?.name || '',
              item_category2: product.primaryCategories?.[1]?.name || '',
              quantity: 1,
            },
          ],
        })
        ampli.eCommerceAddToWishlistItem(analytics)
        return
      }

      ampli.eCommerceRemoveFromWishlistItem(analytics)
    })
  }, [ampli, fetchAnalytics, key, sourceID, store.config?.currencyCode])

  return [exists, toggle] as const
}

export function useWishListCount() {
  const [count, setCount] = useState<number>(0)
  useEffect(() => wishListStateManager.subscribeCount(result => setCount(result)), [])
  return count
}
